import React, { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  withStyles,
} from '@material-ui/core'
import { acceptWorkspaceInvitationService } from 'src/api/workspace-member'
import translator from 'src/translate'
import { useDispatch } from 'react-redux'
import { authenticationLoginUserSuccess } from 'src/reducers/authentication/action'
import { set as setCookie } from 'es-cookie'
import { getCookieOptions } from 'src/utils/cookie-options'
import { track } from '@amplitude/analytics-browser'

const getErrorType = (err) => err?.type || err?.name || 'UnknownError'

// Accepting logs the user in, which re-mounts this page under the signed-in
// layout and runs the effect again. The backend deletes the invitation on the
// first accept, so a second call fails with "Invitation not found or expired"
// even though the user joined. Keep one request per code for the page's life,
// and run its side effects (tracking, cookie, login) only once.
const acceptRequests = new Map()

const acceptInvitationOnce = (code, dispatch) => {
  if (!acceptRequests.has(code)) {
    track('workspace_member_invite_accept', {
      step: 'attempt',
      source: 'workspace_invite_accept_page',
      path: window.location.pathname,
    })

    const request = acceptWorkspaceInvitationService(code).then(
      (res) => {
        track('workspace_member_invite_accept', {
          step: 'success',
          source: 'workspace_invite_accept_page',
          path: window.location.pathname,
          shop_id: res?.data?.shopId || null,
        })
        setCookie('language', res.data.language, getCookieOptions())
        dispatch(authenticationLoginUserSuccess(res.data))
        return res
      },
      (err) => {
        track('workspace_member_invite_accept', {
          step: 'failed',
          source: 'workspace_invite_accept_page',
          path: window.location.pathname,
          error_type: getErrorType(err),
          error_message: err?.message || null,
        })
        throw err
      },
    )

    acceptRequests.set(code, request)
  }

  return acceptRequests.get(code)
}

const styles = (theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  card: {
    maxWidth: 400,
    width: '100%',
    textAlign: 'center',
    padding: theme.spacing(4),
    boxShadow: '0 8px 40px -12px rgba(0,0,0,0.3)',
  },
  logo: {
    height: 60,
    marginBottom: theme.spacing(3),
  },
  button: {
    marginTop: theme.spacing(3),
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 'bold',
  },
})

const WorkspaceInviteAcceptPage = ({ classes }) => {
  const location = useLocation()
  const history = useHistory()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const dispatch = useDispatch()

  const query = new URLSearchParams(location.search)
  const code = query.get('code')

  useEffect(() => {
    if (!code) {
      setError(translator._('Invalid invitation link'))
      setLoading(false)
      return
    }

    acceptInvitationOnce(code, dispatch)
      .then(() => {
        setSuccess(true)
      })
      .catch(() => {
        setError(translator._('Invitation not found or expired'))
      })
      .finally(() => {
        setLoading(false)
      })
  }, [code, dispatch])

  const handleGoHome = () => {
    history.push('/')
  }

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <img src="/assets/logo.png" alt="MCG Logo" className={classes.logo} />

          {loading ? (
            <Box display="flex" flexDirection="column" alignItems="center">
              <CircularProgress color="primary" />
              <Typography variant="body1" style={{ marginTop: 16 }}>
                {translator._('Joining workspace...')}
              </Typography>
            </Box>
          ) : error ? (
            <>
              <Typography variant="h6" color="error" gutterBottom>
                {translator._('Error')}
              </Typography>
              <Typography variant="body1">{error}</Typography>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                className={classes.button}
                onClick={handleGoHome}
              >
                {translator._('Go to Home')}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h5" gutterBottom>
                {translator._('Welcome!')}
              </Typography>
              <Typography variant="body1">
                {translator._('You have joined the workspace!')}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                className={classes.button}
                onClick={handleGoHome}
              >
                {translator._('Go to Home')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default withStyles(styles)(WorkspaceInviteAcceptPage)
